import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { LoginPage } from './auth/LoginPage.jsx';
import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { AppShell } from './components/layout/AppShell.jsx';
import { ModuleGuard } from './components/layout/ModuleGuard.jsx';
import { DemoRequestsPage } from './platform/DemoRequestsPage.jsx';
import { PlatformAuthProvider } from './platform/PlatformAuthContext.jsx';
import { PlatformLoginPage } from './platform/PlatformLoginPage.jsx';
import { PlatformShell } from './platform/PlatformShell.jsx';
import { TenantDetailPage } from './platform/TenantDetailPage.jsx';
import { SetPasswordPage } from './pages/SetPasswordPage.jsx';
import { TenantUsersPage } from './platform/TenantUsersPage.jsx';
import { TenantsPage } from './platform/TenantsPage.jsx';
import { Dashboard } from './pages/Dashboard.jsx';
import { AccountsPage } from './pages/finance/AccountsPage.jsx';
import { FinanceDashboardPage } from './pages/finance/FinanceDashboardPage.jsx';
import { InvoiceDetailPage } from './pages/finance/InvoiceDetailPage.jsx';
import { InvoicePrintPage } from './pages/finance/InvoicePrintPage.jsx';
import { InvoicesPage } from './pages/finance/InvoicesPage.jsx';
import { ReportsPage as FinanceReportsPage } from './pages/finance/ReportsPage.jsx';
import { DunningPage } from './pages/finance/DunningPage.jsx';
import { PaymentRunsPage } from './pages/finance/PaymentRunsPage.jsx';
import { DunningPrintPage } from './pages/finance/DunningPrintPage.jsx';
import { CostCentersPage } from './pages/finance/CostCentersPage.jsx';
import { ControllingPage } from './pages/finance/ControllingPage.jsx';
import { ProfitCentersPage } from './pages/finance/ProfitCentersPage.jsx';
import { DownPaymentsPage } from './pages/finance/DownPaymentsPage.jsx';
import { IntercompanyPage } from './pages/finance/IntercompanyPage.jsx';
import { BudgetsPage } from './pages/finance/BudgetsPage.jsx';
import { CashJournalPage } from './pages/finance/CashJournalPage.jsx';
import { RecurringJournalsPage } from './pages/finance/RecurringJournalsPage.jsx';
import { PaymentTermsPage } from './pages/finance/PaymentTermsPage.jsx';
import { CollectiveInvoicePage } from './pages/finance/CollectiveInvoicePage.jsx';
import { ReportBuilderPage } from './pages/finance/ReportBuilderPage.jsx';
import { MaintenancePage } from './pages/finance/MaintenancePage.jsx';
import { BankReconciliationPage } from './pages/finance/BankReconciliationPage.jsx';
import { CreditNotesPage } from './pages/finance/CreditNotesPage.jsx';
import { CreditNotePrintPage } from './pages/finance/CreditNotePrintPage.jsx';
import { RecurringInvoicesPage } from './pages/finance/RecurringInvoicesPage.jsx';
import { VatReportPage } from './pages/finance/VatReportPage.jsx';
import { FixedAssetsPage } from './pages/finance/FixedAssetsPage.jsx';
import { JournalPage } from './pages/finance/JournalPage.jsx';
import { PeriodClosePage } from './pages/finance/PeriodClosePage.jsx';
import { ApplicationsPage } from './pages/hr/ApplicationsPage.jsx';
import { CandidatesPage } from './pages/hr/CandidatesPage.jsx';
import { EmployeesPage } from './pages/hr/EmployeesPage.jsx';
import { HrDashboardPage } from './pages/hr/HrDashboardPage.jsx';
import { ReviewsPage } from './pages/hr/ReviewsPage.jsx';
import { TimeTrackingPage as HrTimeTrackingPage } from './pages/hr/TimeTrackingPage.jsx';
import { TravelExpensesPage } from './pages/hr/TravelExpensesPage.jsx';
import { JobPostingsPage } from './pages/hr/JobPostingsPage.jsx';
import { LeavePage } from './pages/hr/LeavePage.jsx';
import { LeaveTypesPage } from './pages/hr/LeaveTypesPage.jsx';
import { MonthlyReportPage } from './pages/hr/MonthlyReportPage.jsx';
import { MyLeavePage } from './pages/self/MyLeavePage.jsx';
import { MyTeamPage } from './pages/self/MyTeamPage.jsx';
import { MyTimePage } from './pages/self/MyTimePage.jsx';
import { PayrollPage } from './pages/hr/PayrollPage.jsx';
import { WithholdingTaxPage } from './pages/hr/WithholdingTaxPage.jsx';
import { InventoryDashboardPage } from './pages/inventory/InventoryDashboardPage.jsx';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage.jsx';
import { ProjectsDashboardPage } from './pages/projects/ProjectsDashboardPage.jsx';
import { ProjectsListPage } from './pages/projects/ProjectsListPage.jsx';
import { TimeTrackingPage } from './pages/projects/TimeTrackingPage.jsx';
import { ExpensesPage } from './pages/projects/ExpensesPage.jsx';
import { LowStockPage } from './pages/inventory/LowStockPage.jsx';
import { ProductsPage } from './pages/inventory/ProductsPage.jsx';
import { PurchaseOrderDetailPage } from './pages/inventory/PurchaseOrderDetailPage.jsx';
import { PurchaseOrdersPage } from './pages/inventory/PurchaseOrdersPage.jsx';
import { StockMovementsPage } from './pages/inventory/StockMovementsPage.jsx';
import { SuppliersPage } from './pages/inventory/SuppliersPage.jsx';
import { CycleCountsPage } from './pages/inventory/CycleCountsPage.jsx';
import { RequisitionsPage } from './pages/inventory/RequisitionsPage.jsx';
import { ReservationsPage } from './pages/inventory/ReservationsPage.jsx';
import { LotsPage } from './pages/inventory/LotsPage.jsx';
import { QualityPage } from './pages/inventory/QualityPage.jsx';
import { ConsignmentPage } from './pages/inventory/ConsignmentPage.jsx';
import { ContractsPage as PurchaseContractsPage } from './pages/inventory/ContractsPage.jsx';
import { SchedulingAgreementsPage } from './pages/inventory/SchedulingAgreementsPage.jsx';
import { SerialsPage } from './pages/inventory/SerialsPage.jsx';
import { VendorEvaluationPage } from './pages/inventory/VendorEvaluationPage.jsx';
import { UomPage } from './pages/inventory/UomPage.jsx';
import { BackordersPage } from './pages/inventory/BackordersPage.jsx';
import { MrpRunPage } from './pages/inventory/MrpRunPage.jsx';
import { AtpPage } from './pages/inventory/AtpPage.jsx';
import { BinsPage } from './pages/inventory/BinsPage.jsx';
import { CustomersPage } from './pages/sales/CustomersPage.jsx';
import { CustomerDetailPage } from './pages/sales/CustomerDetailPage.jsx';
import { DeliveriesPage } from './pages/sales/DeliveriesPage.jsx';
import { DeliveryPrintPage } from './pages/sales/DeliveryPrintPage.jsx';
import { OrderDetailPage } from './pages/sales/OrderDetailPage.jsx';
import { OrdersPage } from './pages/sales/OrdersPage.jsx';
import { CreditManagementPage } from './pages/sales/CreditManagementPage.jsx';
import { PipelinePage } from './pages/sales/PipelinePage.jsx';
import { PriceListsPage } from './pages/sales/PriceListsPage.jsx';
import { QuoteDetailPage } from './pages/sales/QuoteDetailPage.jsx';
import { QuotesPage } from './pages/sales/QuotesPage.jsx';
import { ReturnsPage } from './pages/sales/ReturnsPage.jsx';
import { ContractsPage } from './pages/sales/ContractsPage.jsx';
import { TicketsPage } from './pages/sales/TicketsPage.jsx';
import { SalesDashboardPage } from './pages/sales/SalesDashboardPage.jsx';
import { SalesReportsPage } from './pages/sales/SalesReportsPage.jsx';
import { ProductionDashboardPage } from './pages/production/ProductionDashboardPage.jsx';
import { WorkCentersPage } from './pages/production/WorkCentersPage.jsx';
import { BomsPage } from './pages/production/BomsPage.jsx';
import { WorkOrdersPage } from './pages/production/WorkOrdersPage.jsx';
import { WorkOrderDetailPage } from './pages/production/WorkOrderDetailPage.jsx';
import { MrpPage } from './pages/production/MrpPage.jsx';
import { CapacityPlanningPage } from './pages/production/CapacityPlanningPage.jsx';
import { SettingsPage } from './pages/SettingsPage.jsx';
import { InboxPage } from './pages/InboxPage.jsx';
import { ProfilePage } from './pages/ProfilePage.jsx';
import { CalendarPage } from './pages/CalendarPage.jsx';
import { RulesPage } from './pages/automation/RulesPage.jsx';
import { AlertsPage } from './pages/automation/AlertsPage.jsx';

// Public landing page: lazy so the marketing chunk never weighs down the app.
const LandingPage = lazy(() =>
  import('./pages/landing/LandingPage.jsx').then((m) => ({ default: m.LandingPage })),
);

/**
 * Zeigt DIESE Installation Recicas Verkaufsseite?
 *
 * Voreinstellung NEIN, und darin liegt die ganze Entscheidung. Bisher war die
 * Landingpage die abgemeldete Startseite JEDER Installation: wer die Adresse
 * seiner Firma tippte, landete auf der Marketingseite eines fremden
 * Unternehmens, mit "Demo buchen"-Formular, ohne Anmeldeknopf, ohne Impressum.
 *
 * Wer den Schalter vergisst, bekommt jetzt die Anmeldemaske. Umgekehrt wäre
 * das Vergessen peinlich statt harmlos, und genau so herum darf ein
 * Vorgabewert nicht stehen.
 *
 * Als Funktion und nicht als Konstante am Modulkopf: nur so lässt sich BEIDES
 * prüfen. Vite ersetzt den Ausdruck beim Bauen ohnehin durch das Literal, die
 * ungenutzte Verkaufsseite fliegt also weiterhin aus dem Paket.
 */
function zeigtVerkaufsseite() {
  return import.meta.env.VITE_PUBLIC_LANDING === 'true';
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Öffentlich, weil genau das der Punkt ist: wer diesen Link öffnet,
          kommt gerade NICHT mehr hinein. Ausserhalb von AppShell und
          ausserhalb jeder Sitzung. */}
      <Route path="/set-password" element={<SetPasswordPage />} />

      {/* Anbieterebene: eigener Zweig AUSSERHALB von AppShell und ausserhalb der
          Mandantensitzung. Eigenes Konto, eigener Signaturschlüssel, eigener
          HTTP-Client - beide Hüte können gleichzeitig aufliegen, und ein
          abgelaufenes Anbietertoken loggt niemanden aus seiner Buchhaltung aus. */}
      <Route
        path="/platform"
        element={
          <PlatformAuthProvider>
            <Outlet />
          </PlatformAuthProvider>
        }
      >
        <Route index element={<Navigate to="/platform/tenants" replace />} />
        <Route path="login" element={<PlatformLoginPage />} />
        <Route element={<PlatformShell />}>
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="tenants/:id/users" element={<TenantUsersPage />} />
          <Route path="demo-requests" element={<DemoRequestsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/platform/tenants" replace />} />
      </Route>

      <Route
        element={
          <ProtectedRoute
            publicHome={
              zeigtVerkaufsseite() ? (
                <Suspense fallback={null}>
                  <LandingPage />
                </Suspense>
              ) : null
            }
          >
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="finance" element={<ModuleGuard module="finance" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<FinanceDashboardPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="invoices/:id/print" element={<InvoicePrintPage />} />
          <Route path="credit-notes" element={<CreditNotesPage />} />
          <Route path="credit-notes/:id/print" element={<CreditNotePrintPage />} />
          <Route path="recurring" element={<RecurringInvoicesPage />} />
          <Route path="reports" element={<FinanceReportsPage />} />
          <Route path="vat" element={<VatReportPage />} />
          <Route path="dunning" element={<DunningPage />} />
          <Route path="dunning/:id/print" element={<DunningPrintPage />} />
          <Route path="cost-centers" element={<CostCentersPage />} />
          <Route path="controlling" element={<ControllingPage />} />
          <Route path="profit-centers" element={<ProfitCentersPage />} />
          <Route path="down-payments" element={<DownPaymentsPage />} />
          <Route path="intercompany" element={<IntercompanyPage />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="cash-journal" element={<CashJournalPage />} />
          <Route path="recurring-journals" element={<RecurringJournalsPage />} />
          <Route path="payment-terms" element={<PaymentTermsPage />} />
          <Route path="collective-invoices" element={<CollectiveInvoicePage />} />
          <Route path="report-builder" element={<ReportBuilderPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="fixed-assets" element={<FixedAssetsPage />} />
          <Route path="journal" element={<JournalPage />} />
          <Route path="period-close" element={<PeriodClosePage />} />
          <Route path="bank" element={<BankReconciliationPage />} />
          <Route path="payment-runs" element={<PaymentRunsPage />} />
        </Route>
        {/* Die eigenen Daten. Bewusst NICHT unter /hr: der Wachposten dort
            verlangt Zugriff auf die Personalakten aller Kollegen - genau das
            soll für den eigenen Ferienstand nicht nötig sein. */}
        <Route path="meine" element={<ModuleGuard module="self" />}>
          <Route index element={<Navigate to="ferien" replace />} />
          <Route path="ferien" element={<MyLeavePage />} />
          <Route path="zeit" element={<MyTimePage />} />
          {/* Zu meiner Entscheidung. Liegt unter /meine, weil "mein Team"
              meine Sicht ist - und weil ein Vorgesetzter mit der Rolle
              employee unter /hr gar nicht bis zur Zuständigkeitsprüfung
              käme. */}
          <Route path="team" element={<MyTeamPage />} />
        </Route>

        <Route path="hr" element={<ModuleGuard module="hr" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<HrDashboardPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="leave" element={<LeavePage />} />
          {/* Abwesenheitsarten und Feiertage pflegen. Vorher konnte das nur
              ein Entwickler: der Weg zum Anlegen einer Art war fertig
              gebaut und nur nie angebunden. */}
          <Route path="leave-types" element={<LeaveTypesPage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="withholding-tax" element={<WithholdingTaxPage />} />
          <Route path="time" element={<HrTimeTrackingPage />} />
          {/* Der Monatsrapport zum Unterschreiben. Erste Druckseite des
              Personalbereichs - bisher musste für den Treuhänder jemand
              abschreiben. */}
          <Route path="time/report" element={<MonthlyReportPage />} />
          <Route path="travel" element={<TravelExpensesPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="job-postings" element={<JobPostingsPage />} />
          <Route path="candidates" element={<CandidatesPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
        </Route>
        <Route path="inventory" element={<ModuleGuard module="inventory" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<InventoryDashboardPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="low-stock" element={<LowStockPage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="stock" element={<StockMovementsPage />} />
          <Route path="lots" element={<LotsPage />} />
          <Route path="bins" element={<BinsPage />} />
          <Route path="quality" element={<QualityPage />} />
          <Route path="consignment" element={<ConsignmentPage />} />
          <Route path="contracts" element={<PurchaseContractsPage />} />
          <Route path="scheduling-agreements" element={<SchedulingAgreementsPage />} />
          <Route path="serials" element={<SerialsPage />} />
          <Route path="vendor-evaluation" element={<VendorEvaluationPage />} />
          <Route path="units" element={<UomPage />} />
          <Route path="backorders" element={<BackordersPage />} />
          <Route path="mrp-run" element={<MrpRunPage />} />
          <Route path="atp" element={<AtpPage />} />
          <Route path="cycle-counts" element={<CycleCountsPage />} />
          <Route path="requisitions" element={<RequisitionsPage />} />
          <Route path="reservations" element={<ReservationsPage />} />
          <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
        </Route>
        <Route path="sales" element={<ModuleGuard module="sales" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<SalesDashboardPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="price-lists" element={<PriceListsPage />} />
          <Route path="quotes" element={<QuotesPage />} />
          <Route path="quotes/:id" element={<QuoteDetailPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="credit" element={<CreditManagementPage />} />
          <Route path="deliveries" element={<DeliveriesPage />} />
          <Route path="deliveries/:id/print" element={<DeliveryPrintPage />} />
          <Route path="returns" element={<ReturnsPage />} />
          <Route path="contracts" element={<ContractsPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="reports" element={<SalesReportsPage />} />
        </Route>
        <Route path="projects" element={<ModuleGuard module="projects" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ProjectsDashboardPage />} />
          <Route path="list" element={<ProjectsListPage />} />
          <Route path="time" element={<TimeTrackingPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path=":id" element={<ProjectDetailPage />} />
        </Route>
        <Route path="production" element={<ModuleGuard module="production" />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ProductionDashboardPage />} />
          <Route path="boms" element={<BomsPage />} />
          <Route path="work-centers" element={<WorkCentersPage />} />
          <Route path="work-orders" element={<WorkOrdersPage />} />
          <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
          <Route path="mrp" element={<MrpPage />} />
          <Route path="capacity" element={<CapacityPlanningPage />} />
        </Route>
        <Route path="automation" element={<ModuleGuard module="admin" />}>
          <Route index element={<Navigate to="rules" replace />} />
          <Route path="rules" element={<RulesPage />} />
          <Route path="alerts" element={<AlertsPage />} />
        </Route>
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
